import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { hiddenItems, isSingle, itemVisible, type HiddenReason, type NavGroup, type NavItem } from "../../lib/nav";
import { highlight, search, type SearchDoc } from "../../lib/nav-search";
import { useT, type MsgKey } from "../../lib/i18n";
import { findRow, liveDocs, type ContentTags, type LiveContent } from "../../lib/search-content";
import {
  fetchAchievements, fetchActEvents, fetchActItems, fetchAssetInfo, fetchIapConfig, fetchIdols, fetchPiggies, fetchPlayers,
  fetchShareLinks, fetchShopItems, fetchShopSections, fetchTitles,
} from "../../lib/api";

// 玩家详情抽屉在玩家面板那一块里（懒加载）：静态引入会把整个玩家面板拖进主包
const PlayerDetailDrawer = lazy(() => import("../../pages/PlayerPage").then((m) => ({ default: m.PlayerDetailDrawer })));

/**
 * ⌘K 搜索：页面 + 页面里的配置项，回车直接打开；配置项打开后滚到那一格并闪一下。
 *
 * 配置项来自接口视角登记表（约 400 项、80 多 KB）。**只在第一次打开搜索时按需加载**，
 * 不进主包——主包有 500 KiB 的闸（tools/check-web-bundle.mjs）。
 */
type LensLite = { id: string; page: string; label: string; group?: string };

const RECENT_KEY = "aeroplane_admin_search_recent";
function loadRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, 8) : [];
  } catch {
    return [];
  }
}
function pushRecent(key: string) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify([key, ...loadRecent().filter((k) => k !== key)].slice(0, 8))); } catch { /* 存不了就不记 */ }
}

/**
 * 打开配置项所在页后找到那一格：表单行（Field 的 data-api）或表头（DataTable 列的 data-api）。
 * 页是懒加载的，渲染要一会儿，所以轮询；3 秒还没有（比如那一项在弹窗里）就安静放弃，人已经在对的页上了。
 */
function focusField(id: string) {
  // 选择器用 JSON.stringify 拼引号：api-lens.test.ts 按「data-api 等号加双引号」的写法扫页面上的登记 id，字面量会被当成一个没登记的 id
  const sel = `[data-api=${JSON.stringify(id)}]`;
  const started = Date.now();
  const tick = () => {
    const el = document.querySelector<HTMLElement>(sel);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.animate(
        [{ boxShadow: "inset 0 0 0 2px var(--el-color-primary, #409eff)", backgroundColor: "rgba(64,158,255,.12)" }, { boxShadow: "inset 0 0 0 2px transparent", backgroundColor: "transparent" }],
        { duration: 2400, easing: "ease-in" },
      );
      el.querySelector<HTMLElement>("input, select, textarea")?.focus({ preventScroll: true });
      return;
    }
    if (Date.now() - started < 3000) setTimeout(tick, 100);
  };
  setTimeout(tick, 50);
}

/**
 * 活表按代理缓存一分钟：连着搜几次不重复拉，过一会儿再开能看到别人刚加的商品。
 * 哪一张拉失败就少那一段（多人代理没有任务成就等），不挡别的
 */
const LIVE_TTL = 60_000;
let liveCache: { agent: string; at: number; data: LiveContent } | null = null;
async function loadLive(agent: string): Promise<LiveContent> {
  if (liveCache && liveCache.agent === agent && Date.now() - liveCache.at < LIVE_TTL) return liveCache.data;
  const pick = async <T,>(p: Promise<T>): Promise<T | undefined> => { try { return await p; } catch { return undefined; } };
  const [items, sections, asset, iap, idols, ach, titles, actItems, actEvents, piggies, links, erheAch, erheTitles] = await Promise.all([
    pick(fetchShopItems()), pick(fetchShopSections()), pick(fetchAssetInfo()), pick(fetchIapConfig()), pick(fetchIdols()),
    pick(fetchAchievements()), pick(fetchTitles()), pick(fetchActItems()), pick(fetchActEvents()), pick(fetchPiggies()), pick(fetchShareLinks()),
    // 二合那套任务成就：同名的条目落到「二合任务成就管理」下的页
    pick(fetchAchievements("erhe")), pick(fetchTitles("erhe")),
  ]);
  const data: LiveContent = {
    shopItems: items?.items, shopSections: sections?.sections, assetInfo: asset?.rows, iapItems: iap?.items, idols: idols?.idols,
    achievements: ach?.achievements, titles: titles?.titles, actItems: actItems?.items, actEvents: actEvents?.events,
    piggies: piggies?.piggies, shareLinks: links?.items,
    erheAchievements: erheAch?.achievements, erheTitles: erheTitles?.titles,
  };
  liveCache = { agent, at: Date.now(), data };
  return data;
}

/** 玩家 id 存成十进制字符串的字节，hex 解回来就是运营认识的那串数字 */
const uidOf = (hex: string) => { try { return hex.match(/../g)!.map((h) => String.fromCharCode(parseInt(h, 16))).join(""); } catch { return hex; } };

/** 一个泛词（「配置」）别把玩家那段挤到看不见：每段最多这么多条 */
const CAP = { page: 6, field: 8, content: 8, player: 5, hidden: 4 };

/**
 * 内容行：到页上找到那一行，滚过去闪一下。页面懒加载、数据要一会儿，所以轮询；
 * 3 秒找不到（在弹窗里、虚拟滚动没画到）就停在页上
 */
function focusRow(doc: SearchDoc) {
  const started = Date.now();
  const tick = () => {
    const el = findRow(document.querySelector("[data-collab-main]") ?? document, doc);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.animate(
        [{ boxShadow: "inset 0 0 0 2px var(--el-color-primary, #409eff)", backgroundColor: "rgba(64,158,255,.12)" }, { boxShadow: "inset 0 0 0 2px transparent", backgroundColor: "transparent" }],
        { duration: 2400, easing: "ease-in" },
      );
      return;
    }
    if (Date.now() - started < 3000) setTimeout(tick, 120);
  };
  setTimeout(tick, 80);
}

export function SearchPalette({ open, onClose, nav, selectors, isSuperAdmin, isRoot, onPage, agent, getContent }: {
  open: boolean;
  onClose: () => void;
  /** 当前代理实际显示的菜单（已按选择器过滤、已补玩法格） */
  nav: NavGroup[];
  selectors?: { buildingSystem?: string; gameMode?: string };
  isSuperAdmin?: boolean;
  isRoot?: boolean;
  onPage: (id: string) => void;
  /** 当前代理：活表按它缓存 */
  agent?: string | null;
  /** 版本快照里的内容（关卡、建筑、卡包…），App 手里的状态现算；没给就不搜内容 */
  getContent?: (path: (page: string) => string, tags: ContentTags) => SearchDoc[];
}) {
  const t = useT();
  const [live, setLive] = useState<LiveContent | null>(null);
  const [players, setPlayers] = useState<SearchDoc[]>([]);
  const [playersBusy, setPlayersBusy] = useState(false);
  const [playerHex, setPlayerHex] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [lens, setLens] = useState<LensLite[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setSel(0);
    setTimeout(() => inputRef.current?.focus(), 0);
    if (agent) {
      setLive(null);
      loadLive(agent).then(setLive).catch(() => setLive({}));
    }
    if (!lens) {
      import("../../lib/apiLens")
        .then((m) => setLens(m.lensEntries().map((e) => ({ id: e.id, page: e.page, label: e.label, group: e.group }))))
        .catch(() => setLens([]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lens]);

  const name = (it: NavItem) => { const k = `nav.${it.id}`; const s = t(k as MsgKey); return s === k ? it.label : s; };
  const groupName = (g: NavGroup) => t(`navGroup.${g.id}` as MsgKey);
  const reasonText = (r: HiddenReason) => ({
    init: t("search.hidden.init"),
    pettown: t("search.hidden.pettown"),
    levels: t("search.hidden.levels"),
    match: t("search.hidden.match"),
  })[r];

  const tags: ContentTags = {
    level: t("search.tag.level"), building: t("search.tag.building"), reward: t("search.tag.reward"), pack: t("search.tag.pack"),
    card: t("search.tag.card"), spin: t("search.tag.spin"), pet: t("search.tag.pet"), frame: t("search.tag.frame"),
    stage: t("search.tag.stage"), ability: t("search.tag.ability"), channel: t("search.tag.channel"), talent: t("search.tag.talent"),
    slot: t("search.tag.slot"), shopItem: t("search.tag.shopItem"), shopSection: t("search.tag.shopSection"), assetInfo: t("search.tag.assetInfo"),
    iap: t("search.tag.iap"), idol: t("search.tag.idol"), task: t("search.tag.task"), achievement: t("search.tag.achievement"),
    title: t("search.tag.title"), actItem: t("search.tag.actItem"), actEvent: t("search.tag.actEvent"), piggy: t("search.tag.piggy"),
    shareLink: t("search.tag.shareLink"),
  };

  // 玩家：停手 250ms 再查；两个字以上、纯数字（玩家 id）或一个汉字才查，免得一个字母拉一页人
  useEffect(() => {
    const kw = q.trim();
    if (!open || !(kw.length >= 2 || /^\d+$/.test(kw) || /[^\x00-\x7f]/.test(kw))) { setPlayers([]); setPlayersBusy(false); return; }
    let alive = true;
    setPlayersBusy(true);
    const h = setTimeout(() => {
      fetchPlayers({ kw, size: CAP.player })
        .then((r) => {
          if (!alive) return;
          setPlayers(r.rows.map((p) => {
            const uid = uidOf(p.userIdHex);
            // 先显示玩家自己改的昵称（游戏里别人看到的就是它），平台昵称放第二行
            const shown = p.customNickname || p.nickname || uid;
            const other = p.customNickname && p.nickname && p.nickname !== p.customNickname ? ` · ${p.nickname}` : "";
            return { kind: "player", key: `player:${p.userIdHex}`, page: "report-players", hex: p.userIdHex, title: shown,
              path: `${t("search.playerId")} ${uid}${other} · ${t("search.playerLevel", { n: p.cityLevel })}`,
              alt: [uid, p.userIdHex, p.nickname ?? "", p.customNickname ?? ""] } satisfies SearchDoc;
          }));
        })
        .catch(() => { if (alive) setPlayers([]); })
        .finally(() => { if (alive) setPlayersBusy(false); });
    }, 250);
    return () => { alive = false; clearTimeout(h); };
  }, [q, open, t]);

  const docs = useMemo<SearchDoc[]>(() => {
    // 外壳每次渲染都会重算菜单，面板关着时不建索引
    if (!open) return [];
    const out: SearchDoc[] = [];
    const pagePath = new Map<string, string>();
    for (const g of nav) {
      for (const it of g.items) {
        if (!itemVisible(it, { isSuperAdmin, isRoot })) continue;
        const gp = isSingle(g) ? "" : groupName(g);
        pagePath.set(it.id, [gp, name(it)].filter(Boolean).join(" / "));
        out.push({ kind: "page", key: it.id, page: it.id, title: name(it), path: gp, alt: [it.label, it.id, g.label] });
        for (const c of it.children ?? []) {
          pagePath.set(c.id, [gp, name(it), name(c)].filter(Boolean).join(" / "));
          out.push({ kind: "page", key: c.id, page: c.id, title: name(c), path: [gp, name(it)].filter(Boolean).join(" / "), alt: [c.label, c.id, it.label] });
        }
      }
    }
    for (const h of hiddenItems(selectors)) {
      out.push({ kind: "page", key: h.item.id, page: h.item.id, title: name(h.item), path: groupName(h.group), alt: [h.item.label, h.item.id], hidden: reasonText(h.reason) });
    }
    for (const e of lens ?? []) {
      const where = pagePath.get(e.page);
      if (!where) continue; // 这一页当前代理没有：配置项不单独露出，页本身已在上面带着原因
      out.push({ kind: "field", key: e.id, page: e.page, title: e.label, path: [where, e.group].filter(Boolean).join(" / "), alt: [e.group ?? "", e.id] });
    }
    // 内容：只收当前代理菜单里有的页上的（多人代理搜不到单人的卡包）
    const pathOf = (pg: string) => pagePath.get(pg) ?? pg;
    for (const d of [...(getContent?.(pathOf, tags) ?? []), ...(live ? liveDocs(live, tags, pathOf) : [])]) {
      if (pagePath.has(d.page)) out.push(d);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nav, lens, live, getContent, selectors?.buildingSystem, selectors?.gameMode, isSuperAdmin, isRoot, t]);

  const results = useMemo(() => {
    if (q.trim()) return search(docs, q);
    const byKey = new Map(docs.map((d) => [d.key, d]));
    return loadRecent().map((k) => byKey.get(k)).filter((d): d is SearchDoc => !!d && !d.hidden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, q, open]);

  // 玩家、活表是后到的，插进来会挪动序号：到了就回到第一条，免得回车开到别的东西
  useEffect(() => setSel(0), [q, players, live]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${sel}"]`)?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  const drawer = playerHex ? (
    <Suspense fallback={null}>
      <PlayerDetailDrawer hex={playerHex} onClose={() => setPlayerHex(null)} />
    </Suspense>
  ) : null;
  if (!open) return drawer;

  // 页面在前、配置项在后分两段显示；键盘顺序与显示顺序一致
  // 当前代理用不上的页放最后单独一段：打不开的东西不该占第一位、被回车选中
  const pages = results.filter((d) => d.kind === "page" && !d.hidden).slice(0, CAP.page);
  const fields = results.filter((d) => d.kind === "field").slice(0, CAP.field);
  const contents = results.filter((d) => d.kind === "content").slice(0, CAP.content);
  const hidden = results.filter((d) => d.hidden).slice(0, CAP.hidden);
  const ordered = q.trim() ? [...pages, ...fields, ...contents, ...players, ...hidden] : results;
  const choose = (d: SearchDoc | undefined) => {
    if (!d || d.hidden) return;
    onClose();
    // 玩家：原地打开详情抽屉，不离开当前页
    if (d.kind === "player") { setPlayerHex(d.hex ?? null); return; }
    pushRecent(d.key);
    d.go?.();
    onPage(d.page);
    if (d.kind === "field") focusField(d.key);
    if (d.kind === "content") focusRow(d);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((i) => Math.min(i + 1, ordered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(ordered[sel]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  const idxOf = (d: SearchDoc) => ordered.indexOf(d);
  const current = ordered[sel];

  const row = (d: SearchDoc) => {
    const i = idxOf(d);
    return (
      <div
        key={`${d.kind}:${d.key}`}
        data-idx={i}
        onMouseMove={() => setSel(i)}
        onClick={() => choose(d)}
        className={
          "flex items-center gap-3 rounded-base px-3 py-2 " +
          (d.hidden ? "cursor-default opacity-60 " : "cursor-pointer ") +
          (current === d ? "bg-primary-50" : "")
        }
      >
        <span className={"w-[52px] flex-none truncate rounded-sm px-1.5 py-px text-center text-xs " + (
          d.kind === "page" ? "bg-fill text-ink-regular" : d.kind === "field" ? "bg-primary-50 text-primary"
            : d.kind === "player" ? "bg-success-50 text-success-800" : "bg-warning-50 text-warning-800")}>
          {d.kind === "page" ? t("search.kind.page") : d.kind === "field" ? t("search.kind.field") : d.kind === "player" ? t("search.kind.player") : d.tag}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base text-ink">
            {highlight(d.title, q).map(([s, m], k) => (m ? <b key={k} className="font-semibold text-primary">{s}</b> : <span key={k}>{s}</span>))}
          </span>
          {(d.path || d.hidden) && (
            <span className="block truncate text-xs text-ink-secondary">
              {d.path}{d.hidden ? `${d.path ? " · " : ""}${d.hidden}` : ""}
            </span>
          )}
        </span>
        {current === d && !d.hidden && <span className="flex-none text-xs text-ink-placeholder">↵</span>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/30 pt-[12vh]" onMouseDown={onClose} data-lens-ui>
      <div className="flex max-h-[70vh] w-[640px] max-w-[92vw] flex-col overflow-hidden rounded-base bg-white shadow-base" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-line-lighter px-4">
          <SearchIcon className="h-4 w-4 flex-none text-ink-placeholder" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={t("search.placeholder")}
            className="h-12 min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-placeholder"
          />
          <kbd className="flex-none rounded-sm border border-line px-1.5 text-xs text-ink-placeholder">Esc</kbd>
        </div>
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-2">
          {q.trim() ? (
            ordered.length || playersBusy ? (
              <>
                {pages.length > 0 && <div className="px-3 pb-1 pt-2 text-xs font-semibold text-ink-secondary">{t("search.section.pages")}</div>}
                {pages.map(row)}
                {fields.length > 0 && <div className="px-3 pb-1 pt-3 text-xs font-semibold text-ink-secondary">{t("search.section.fields")}</div>}
                {fields.map(row)}
                {contents.length > 0 && <div className="px-3 pb-1 pt-3 text-xs font-semibold text-ink-secondary">{t("search.section.content")}</div>}
                {contents.map(row)}
                {(players.length > 0 || playersBusy) && <div className="px-3 pb-1 pt-3 text-xs font-semibold text-ink-secondary">{t("search.section.players")}</div>}
                {players.map(row)}
                {playersBusy && !players.length && <div className="px-3 py-2 text-xs text-ink-placeholder">{t("search.loadingPlayers")}</div>}
                {hidden.length > 0 && <div className="px-3 pb-1 pt-3 text-xs font-semibold text-ink-secondary">{t("search.section.hidden")}</div>}
                {hidden.map(row)}
                {!lens && <div className="px-3 py-2 text-xs text-ink-placeholder">{t("search.loadingFields")}</div>}
              </>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-ink-secondary">
                {lens && (live || !agent) ? t("search.empty", { q: q.trim() }) : t("search.loadingFields")}
              </div>
            )
          ) : results.length ? (
            <>
              <div className="px-3 pb-1 pt-2 text-xs font-semibold text-ink-secondary">{t("search.section.recent")}</div>
              {results.map(row)}
            </>
          ) : (
            <div className="px-3 py-8 text-center text-sm text-ink-secondary">{t("search.tip")}</div>
          )}
        </div>
        <div className="flex items-center gap-4 border-t border-line-lighter px-4 py-2 text-xs text-ink-placeholder">
          <span>{t("search.keys")}</span>
        </div>
      </div>
    </div>
  );
}

export function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden className={className}>
      <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
